<template>
  <Teleport to="body">
    <Transition name="backdrop">
      <div
        v-show="open"
        class="fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px] transition-all duration-500"
        @click="close"
      />
    </Transition>

    <Transition name="sheet">
      <div
        v-show="open"
        ref="sheetRef"
        class="fixed bottom-0 left-0 right-0 z-50 m-auto max-w-xl overflow-hidden rounded-t-2xl border-2 border-b-0 border-black-dark bg-white dark:bg-kekkle-dark"
        :class="shadowClass"
        :style="sheetStyle"
      >
        <!-- Drag Handle -->
        <div
          class="flex cursor-grab justify-center bg-white pb-2 pt-3 active:cursor-grabbing dark:bg-kekkle-dark"
          @touchstart="handleTouchStart"
          @touchmove="handleTouchMove"
          @touchend="handleTouchEnd"
          @mousedown="handleMouseDown"
        >
          <div class="h-1.5 w-12 rounded-full bg-gray-300 dark:bg-gray-600" />
        </div>

        <!-- Content -->
        <div
          class="overflow-y-auto bg-white dark:bg-kekkle-dark"
          :class="paddingClass"
          :style="contentStyle"
          data-no-drag
        >
          <slot />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onUnmounted } from "vue";
import type { ShadowColor, PaddingSize } from "@/types/ui/kekkle";

type Props = {
  shadow?: ShadowColor;
  padding?: PaddingSize;
  initialHeight?: "half" | "full";
  snapPoints?: number[]; // Percentage of viewport height
  skipHistoryCleanup?: boolean; // Set to true when navigation is intended
};

const props = withDefaults(defineProps<Props>(), {
  shadow: "yellow",
  padding: "normal",
  initialHeight: "half",
  snapPoints: () => [50, 100],
  skipHistoryCleanup: false,
});

const open = defineModel<boolean>("open", { required: true });

const emit = defineEmits<{
  (e: "close"): void;
  (e: "fullscreen", value: boolean): void;
}>();

const sheetRef = ref<HTMLDivElement | null>(null);
const isFullscreen = ref(false);
const isDragging = ref(false);
const startY = ref(0);
const startHeight = ref(0);
const currentHeight = ref(60); // Percentage of viewport height (default 60%)
const swipeVelocity = ref(0);
const lastY = ref(0);
const lastTime = ref(0);
const historyStateAdded = ref(false);
let isUnmounting = false;

const MAX_HEIGHT = 94; // Maximum height percentage (leaves ~50px gap at top)

// Get dynamic viewport height (accounts for mobile browser address bars)
function getDynamicViewportHeight(): number {
  // Use visualViewport for more accurate mobile support, fallback to window.innerHeight
  return window.visualViewport?.height ?? window.innerHeight;
}

const shadowClass = computed(() => `shadow-${props.shadow}-hard`);

const paddingClass = computed(() => {
  switch (props.padding) {
    case "small":
      return "p-2";
    case "normal":
      return "p-4 md:p-6";
    case "large":
      return "p-6 md:p-8";
    case "none":
      return "";
    default:
      return "p-4 md:p-6";
  }
});

const sheetStyle = computed(() => {
  const heightPercent = Math.min(currentHeight.value, MAX_HEIGHT);
  // Use dvh (dynamic viewport height) which accounts for mobile browser address bars
  const height = `${heightPercent}dvh`;
  const maxHeight = `${MAX_HEIGHT}dvh`;
  return {
    height,
    maxHeight,
    // Only transition height when dragging (resizing)
    // Don't transition during enter/leave - that's handled by Vue transition (transform)
    transition: isDragging.value
      ? "none"
      : "height 0.3s cubic-bezier(0.32, 0.72, 0, 1)",
  };
});

const contentStyle = computed(() => {
  return {
    height: "calc(100% - 40px)", // Subtract drag handle height
  };
});

// Handle browser back button to close sheet
function handlePopState() {
  if (open.value && historyStateAdded.value) {
    // Back button was pressed, close the sheet without manipulating history
    historyStateAdded.value = false;
    open.value = false;
    emit("close");
    document.body.style.overflow = "";
    nextTick(() => {
      isFullscreen.value = false;
      currentHeight.value = 60;
    });
  }
}

watch(open, (newVal) => {
  if (newVal) {
    // Reset state when opening
    isFullscreen.value = props.initialHeight === "full";
    currentHeight.value = props.initialHeight === "full" ? MAX_HEIGHT : 60;
    document.body.style.overflow = "hidden";

    // Add a history state so back button closes the sheet.
    // Spread the existing history.state so Vue Router's internal bookkeeping
    // (back/current/forward/position) is preserved. Replacing it wholesale
    // poisons historyState, making a later router.push build a URL like
    // "https://app.kekkle.nlundefined" and crash the webview.
    if (!historyStateAdded.value) {
      window.history.pushState(
        { ...window.history.state, kekkleBottomSheet: true },
        "",
        window.location.href,
      );
      historyStateAdded.value = true;
      window.addEventListener("popstate", handlePopState);
    }
  } else {
    document.body.style.overflow = "";

    // Remove history state if sheet is closed programmatically (not via back button)
    if (historyStateAdded.value) {
      historyStateAdded.value = false;
      window.removeEventListener("popstate", handlePopState);

      // Only go back if navigation wasn't intended and we're not unmounting
      // During route changes, unmount triggers this watcher — calling go(-1)
      // would pop the new route instead of the fake bottom sheet entry
      if (!props.skipHistoryCleanup && !isUnmounting) {
        window.history.go(-1);
      }
    }
  }
});

// Clean up on unmount. Flag prevents the open watcher from calling history.go(-1)
// during route changes, which would pop the NEW route instead of the fake entry.
onUnmounted(() => {
  isUnmounting = true;
  window.removeEventListener("popstate", handlePopState);
  document.body.style.overflow = "";
});

function close() {
  // Setting open to false will trigger the watcher which handles history
  open.value = false;
  emit("close");
  nextTick(() => {
    isFullscreen.value = false;
    currentHeight.value = 60;
  });
}

function handleTouchStart(e: TouchEvent) {
  if ((e.target as HTMLElement).closest("[data-no-drag]")) return;
  const touch = e.touches[0];
  if (!touch) return;

  isDragging.value = true;
  startY.value = touch.clientY;
  startHeight.value = currentHeight.value;
  lastY.value = touch.clientY;
  lastTime.value = Date.now();
  swipeVelocity.value = 0;
}

function handleTouchMove(e: TouchEvent) {
  if (!isDragging.value) return;

  const touch = e.touches[0];
  if (!touch) return;

  const currentY = touch.clientY;
  const now = Date.now();
  const timeDiff = now - lastTime.value;

  // Calculate velocity (pixels per millisecond)
  if (timeDiff > 0) {
    swipeVelocity.value = (currentY - lastY.value) / timeDiff;
  }

  lastY.value = currentY;
  lastTime.value = now;

  const deltaY = currentY - startY.value;
  const viewportHeight = getDynamicViewportHeight();

  // Convert pixel movement to percentage
  const deltaPercent = (deltaY / viewportHeight) * 100;

  // Calculate new height (dragging down decreases height, dragging up increases it)
  let newHeight = startHeight.value - deltaPercent;

  // Clamp between 20% and MAX_HEIGHT
  newHeight = Math.max(20, Math.min(MAX_HEIGHT, newHeight));

  currentHeight.value = newHeight;
}

function handleTouchEnd() {
  if (!isDragging.value) return;

  isDragging.value = false;

  // Use velocity to determine swipe intent
  const velocityThreshold = 0.3; // pixels per millisecond
  const isSwipeDown = swipeVelocity.value > velocityThreshold;
  const isSwipeUp = swipeVelocity.value < -velocityThreshold;

  // If strong swipe detected, follow the swipe direction
  if (isSwipeDown) {
    // Swipe down
    if (currentHeight.value > 75) {
      // From fullscreen -> default (60%)
      currentHeight.value = 60;
      isFullscreen.value = false;
      emit("fullscreen", false);
    } else {
      // From default -> close
      close();
    }
  } else if (isSwipeUp) {
    // Swipe up -> go fullscreen
    currentHeight.value = MAX_HEIGHT;
    isFullscreen.value = true;
    emit("fullscreen", true);
  } else {
    // No strong swipe, snap based on position
    if (currentHeight.value < 40) {
      // Close if dragged below 40%
      close();
    } else if (currentHeight.value < 77) {
      // Snap to default (60%)
      currentHeight.value = 60;
      isFullscreen.value = false;
      emit("fullscreen", false);
    } else {
      // Snap to fullscreen
      currentHeight.value = MAX_HEIGHT;
      isFullscreen.value = true;
      emit("fullscreen", true);
    }
  }
}

// Mouse events for desktop support
function handleMouseDown(e: MouseEvent) {
  if ((e.target as HTMLElement).closest("[data-no-drag]")) return;

  isDragging.value = true;
  startY.value = e.clientY;
  startHeight.value = currentHeight.value;
  lastY.value = e.clientY;
  lastTime.value = Date.now();
  swipeVelocity.value = 0;

  const handleMouseMove = (moveEvent: MouseEvent) => {
    if (!isDragging.value) return;

    const currentY = moveEvent.clientY;
    const now = Date.now();
    const timeDiff = now - lastTime.value;

    // Calculate velocity (pixels per millisecond)
    if (timeDiff > 0) {
      swipeVelocity.value = (currentY - lastY.value) / timeDiff;
    }

    lastY.value = currentY;
    lastTime.value = now;

    const deltaY = currentY - startY.value;
    const viewportHeight = getDynamicViewportHeight();

    // Convert pixel movement to percentage
    const deltaPercent = (deltaY / viewportHeight) * 100;

    // Calculate new height (dragging down decreases height, dragging up increases it)
    let newHeight = startHeight.value - deltaPercent;

    // Clamp between 20% and MAX_HEIGHT
    newHeight = Math.max(20, Math.min(MAX_HEIGHT, newHeight));

    currentHeight.value = newHeight;
  };

  const handleMouseUp = () => {
    handleTouchEnd();
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  };

  document.addEventListener("mousemove", handleMouseMove);
  document.addEventListener("mouseup", handleMouseUp);
}
</script>

<style scoped>
.backdrop-enter-active {
  transition:
    opacity 0.3s ease,
    backdrop-filter 0.3s ease;
}

.backdrop-leave-active {
  transition:
    opacity 0.5s ease,
    backdrop-filter 0.5s ease;
}

.backdrop-enter-from,
.backdrop-leave-to {
  opacity: 0;
  backdrop-filter: blur(0px);
}

.sheet-enter-active,
.sheet-leave-active {
  transition: transform 0.3s ease-out !important;
}

.sheet-enter-from,
.sheet-leave-to {
  transform: translateY(100%);
}
</style>
